import type { QuestionRadiogroupModelData } from '@coorum-research/shared-common/types'
import type { SurveyElementForm } from './survey-element-form'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { useState } from 'react'
import { cn } from '../../utils'
import { ElementCommentField } from './ElementCommentField'
import { ElementHeader } from './ElementHeader'
import { ElementOtherTextField } from './ElementOtherTextField'

import { ElementValidationMessage } from './ElementValidationMessage'
import { SurveyElementLayout } from './SurveyElementLayout'

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('relative', className)}
      {...props}
    />
  )
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'group-hover:bg-[var(--theme-bg-color)] size-[24px] focus:outline-[var(--theme-outline-color)] outline outline-1 outline-[#909190] shrink-0 rounded-full shadow-xs transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-[var(--theme-bg-color)] data-[state=checked]:outline-[var(--theme-bg-color)] transition-all duration-300',
        'after:absolute after:inset-0',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <div className="absolute left-1/2 top-1/2 size-[8px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

function Label({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'label'>) {
  return (
    <label
      data-slot="radio-group-label"
      className={cn('font-survey text-base font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[var(--theme-text-color)]', className)}
      {...props}
    />
  )
}

type Prop = {
  currentForm: SurveyElementForm
  validationError: string | null
  onValidationError: (errorMessage: string) => void
  onClickNext?: () => void
}

export function SurveyElementRadiogroup({
  currentForm,
  validationError,
  onValidationError,
  onClickNext,
}: Prop) {
  const currentElement = currentForm.elementJson as QuestionRadiogroupModelData
  const selectOptions = currentForm.getSelectOptions()
  const [value, setValue] = useState(() => currentForm.getSelectOptionValue() as string)

  let otherTextValue = ''
  const currentOptionValue = currentForm.getSelectOptionValue()
  if (currentOptionValue === 'other') {
    otherTextValue = currentForm.getValue() as string
  } else {
    otherTextValue = ''
  }

  return (
    <SurveyElementLayout>
      <ElementValidationMessage
        validationError={validationError}
      />
      <ElementHeader
        currentElement={currentElement}
      />
      <RadioGroup
        value={value}
        onValueChange={(value) => {
          setValue(value)
          currentForm.setElementResultRecordValue(value)
          onValidationError('')

          const optionValue = currentForm.getSelectOptionValue()
          if (optionValue !== 'other') {
            if (!currentElement.showCommentArea) {
              onClickNext?.()
            }
          }
        }}
      >
        {selectOptions.map(option => (
          <div
            key={String(option.value)}
            className="group relative flex items-center gap-3 py-[12px]"
          >
            <RadioGroupItem
              value={String(option.value)}
              id={String(option.value)}
            />
            <Label
              htmlFor={String(option.value)}
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {value === 'other' && (
        <ElementOtherTextField
          defaultValue={otherTextValue}
          onChange={(e) => {
            currentForm.setElementResultRecordOtherValue(e.target.value || '')
            onValidationError('')
          }}
        />
      )}
      {currentElement.showCommentArea && (
        <ElementCommentField
          label={currentElement.commentText}
          placeholder={currentElement.commentPlaceholder}
          defaultValue={currentForm.getComment()}
          onChange={(e) => {
            currentForm.setElementResultRecordComment(e.target.value || '')
          }}
        />
      )}
    </SurveyElementLayout>
  )
}
